import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

const RevenueTracking = () => {
  const [revenueData, setRevenueData] = useState({
    totalRevenue: 0,
    editorialRevenue: 0,
    affiliateRevenue: 0,
    foundingMemberRevenue: 0,
    topEditorials: [],
    recentTransactions: [],
    conversionStats: {}
  });
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('month'); // week, month, quarter, year
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadRevenueData();
  }, [timeframe]);

  const loadRevenueData = async () => {
    setIsLoading(true);
    try {
      // Calculate date range based on timeframe
      const endDate = new Date();
      let startDate = new Date();
      
      switch (timeframe) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          startDate.setMonth(endDate.getMonth() - 1);
      }

      // Load editorial attribution data
      const attributionQuery = query(
        collection(db, 'editorialAttribution'),
        where('timestamp', '>=', startDate),
        where('timestamp', '<=', endDate),
        orderBy('timestamp', 'desc')
      );
      
      const attributionSnapshot = await getDocs(attributionQuery);
      const attributionData = [];
      
      attributionSnapshot.forEach(doc => {
        attributionData.push({ id: doc.id, ...doc.data() });
      });

      // Load product clicks
      const clicksQuery = query(
        collection(db, 'productClicks'),
        where('timestamp', '>=', startDate),
        where('timestamp', '<=', endDate),
        orderBy('timestamp', 'desc')
      );
      
      const clicksSnapshot = await getDocs(clicksQuery);
      const clicksData = [];
      
      clicksSnapshot.forEach(doc => {
        clicksData.push({ id: doc.id, ...doc.data() });
      });

      // Process data for dashboard
      const processedData = processRevenueData(attributionData, clicksData);
      setRevenueData(processedData);
      
    } catch (error) {
      console.error('Error loading revenue data:', error);
      setMessage('❌ Error loading revenue data');
    } finally {
      setIsLoading(false);
    }
  };

  const processRevenueData = (attributionData, clicksData) => {
    // Calculate editorial revenue (estimated based on clicks and average commission)
    const editorialClicks = attributionData.length;
    const estimatedConversionRate = 0.15; // 15% conversion rate
    const averageOrderValue = 85; // Average order value
    const averageCommission = 0.06; // 6% commission
    
    const estimatedEditorialRevenue = editorialClicks * estimatedConversionRate * averageOrderValue * averageCommission;

    // Group by editorial for top performers
    const editorialPerformance = {};
    attributionData.forEach(item => {
      if (item.editorialId) {
        if (!editorialPerformance[item.editorialId]) {
          editorialPerformance[item.editorialId] = {
            editorialId: item.editorialId,
            editorialTitle: item.editorialTitle || 'Unknown Editorial',
            clicks: 0,
            estimatedRevenue: 0
          };
        }
        editorialPerformance[item.editorialId].clicks++;
        editorialPerformance[item.editorialId].estimatedRevenue += (averageOrderValue * averageCommission * estimatedConversionRate);
      }
    });

    const topEditorials = Object.values(editorialPerformance)
      .sort((a, b) => b.estimatedRevenue - a.estimatedRevenue)
      .slice(0, 5);

    // No fake founding member revenue - only show real data
    const foundingMemberRevenue = 0; // Would come from Stripe when implemented

    return {
      totalRevenue: estimatedEditorialRevenue + foundingMemberRevenue,
      editorialRevenue: estimatedEditorialRevenue,
      affiliateRevenue: estimatedEditorialRevenue, // Same as editorial for now
      foundingMemberRevenue: foundingMemberRevenue,
      topEditorials: topEditorials,
      recentTransactions: [...attributionData, ...clicksData].slice(0, 10),
      conversionStats: {
        totalClicks: editorialClicks,
        estimatedConversions: Math.round(editorialClicks * estimatedConversionRate),
        conversionRate: editorialClicks > 0 ? (estimatedConversionRate * 100).toFixed(1) + '%' : '0%'
      }
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '0.629rem', textAlign: 'center' }}>
        <div>Loading revenue data...</div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '952px',
      margin: '0.629rem auto',
      padding: '0.629rem',
      backgroundColor: 'white',
      borderRadius: '3.774px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.629rem' }}>
        <h1 style={{ margin: 0, color: '#333' }}>Revenue Tracking</h1>
        
        <div>
          <label style={{ marginRight: '0.157rem', color: '#666' }}>Timeframe:</label>
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            style={{
              padding: '0.157rem',
              border: '1px solid #ddd',
              borderRadius: '1.884px',
              fontSize: '0.283rem'
            }}
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
        </div>
      </div>

      {/* Setup Status */}
      <div style={{
        padding: '0.472rem',
        backgroundColor: '#f0f8ff',
        borderRadius: '2.516rem',
        border: '1px solid #b3d9ff',
        marginBottom: '0.629rem'
      }}>
        <h3 style={{ margin: '0 0 0.314rem 0', color: '#0056b3' }}>📊 Revenue Tracking Setup</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(78.88px, 1fr))', gap: '0.314rem' }}>
          <div>
            <div style={{ fontWeight: 'bold', color: '#333' }}>Editorial Tracking:</div>
            <div style={{ fontSize: '0.283rem', color: '#666' }}>Ready - tracks editorial → shop conversions</div>
          </div>
          <div>
            <div style={{ fontWeight: 'bold', color: '#333' }}>Affiliate Revenue:</div>
            <div style={{ fontSize: '0.283rem', color: '#666' }}>Amazon affiliate commissions tracked</div>
          </div>
          <div>
            <div style={{ fontWeight: 'bold', color: '#333' }}>Founding Members:</div>
            <div style={{ fontSize: '0.283rem', color: '#666' }}>Stripe integration needed</div>
          </div>
          <div>
            <div style={{ fontWeight: 'bold', color: '#333' }}>Brand Partnerships:</div>
            <div style={{ fontSize: '0.283rem', color: '#666' }}>Signal revenue tracking ready</div>
          </div>
        </div>
      </div>

      {/* Revenue Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(78.88px, 1fr))', 
        gap: '0.472rem',
        marginBottom: '0.629rem'
      }}>
        <div style={{
          padding: '0.694rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '3.699px',
          border: revenueData.totalRevenue > 0 ? '2px solid #28a745' : '1px solid #ddd'
        }}>
          <h3 style={{ margin: '0 0 0.231rem 0', color: revenueData.totalRevenue > 0 ? '#28a745' : '#666' }}>Total Revenue</h3>
          <div style={{ fontSize: '0.925rem', fontWeight: 'bold', color: revenueData.totalRevenue > 0 ? '#28a745' : '#666' }}>
            {formatCurrency(revenueData.totalRevenue)}
          </div>
          <div style={{ fontSize: '0.370rem', color: '#888' }}>
            {revenueData.totalRevenue > 0 ? 'Active revenue streams' : 'No revenue data yet'}
          </div>
        </div>

        <div style={{
          padding: '0.694rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '3.699px',
          border: '1px solid #ddd'
        }}>
          <h3 style={{ margin: '0 0 0.231rem 0', color: '#666' }}>Editorial Revenue</h3>
          <div style={{ fontSize: '0.694rem', fontWeight: 'bold', color: '#333' }}>
            {formatCurrency(revenueData.editorialRevenue)}
          </div>
          <div style={{ fontSize: '0.370rem', color: '#888' }}>
            {revenueData.editorialRevenue > 0 ? 'From editorial-to-shop conversions' : 'Waiting for user interactions'}
          </div>
        </div>

        <div style={{
          padding: '0.694rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '3.699px',
          border: '1px solid #ddd'
        }}>
          <h3 style={{ margin: '0 0 0.231rem 0', color: '#666' }}>Founding Members</h3>
          <div style={{ fontSize: '0.694rem', fontWeight: 'bold', color: '#333' }}>
            {formatCurrency(revenueData.foundingMemberRevenue)}
          </div>
          <div style={{ fontSize: '0.370rem', color: '#888' }}>
            {revenueData.foundingMemberRevenue > 0 
              ? `${Math.round(revenueData.foundingMemberRevenue / 99)} members × $99`
              : 'Stripe integration pending'
            }
          </div>
        </div>

        <div style={{
          padding: '0.694rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '3.699px',
          border: '1px solid #ddd'
        }}>
          <h3 style={{ margin: '0 0 0.231rem 0', color: '#666' }}>Conversion Rate</h3>
          <div style={{ fontSize: '0.694rem', fontWeight: 'bold', color: '#333' }}>
            {revenueData.conversionStats.conversionRate}
          </div>
          <div style={{ fontSize: '0.370rem', color: '#888' }}>
            {revenueData.conversionStats.estimatedConversions} of {revenueData.conversionStats.totalClicks} clicks
          </div>
        </div>
      </div>

      {/* Top Performing Editorials */}
      <div style={{ marginBottom: '0.925rem' }}>
        <h2 style={{ marginBottom: '0.462rem', color: '#333' }}>Top Performing Editorials</h2>
        
        {revenueData.topEditorials.length > 0 ? (
          <div style={{ 
            border: '1px solid #ddd', 
            borderRadius: '3.699px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '0.462rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Editorial</th>
                  <th style={{ padding: '0.462rem', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Clicks</th>
                  <th style={{ padding: '0.462rem', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Est. Revenue</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.topEditorials.map((editorial, index) => (
                  <tr key={editorial.editorialId} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.462rem' }}>
                      <div style={{ fontWeight: '600' }}>{editorial.editorialTitle}</div>
                      <div style={{ fontSize: '0.544rem', color: '#666' }}>ID: {editorial.editorialId}</div>
                    </td>
                    <td style={{ padding: '0.462rem', textAlign: 'right' }}>{editorial.clicks}</td>
                    <td style={{ padding: '0.462rem', textAlign: 'right', fontWeight: '600', color: '#28a745' }}>
                      {formatCurrency(editorial.estimatedRevenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            padding: '1.387rem', 
            textAlign: 'center', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '3.699px',
            color: '#666'
          }}>
            <div style={{ fontSize: '1.387rem', marginBottom: '0.462rem' }}>📈</div>
            <h3 style={{ margin: '0 0 0.231rem 0', color: '#333' }}>No Editorial Revenue Yet</h3>
            <p style={{ margin: 0 }}>Editorial-to-shop tracking will appear here as users click "Shop Similar" buttons in your editorials.</p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div>
        <h2 style={{ marginBottom: '0.462rem', color: '#333' }}>Recent Activity</h2>
        
        {revenueData.recentTransactions.length > 0 ? (
          <div style={{ 
            border: '1px solid #ddd', 
            borderRadius: '3.699px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '0.462rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                  <th style={{ padding: '0.462rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Source</th>
                  <th style={{ padding: '0.462rem', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {revenueData.recentTransactions.map((transaction, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.462rem' }}>
                      {transaction.editorialId ? '📝 Editorial Click' : '🛍️ Product Click'}
                    </td>
                    <td style={{ padding: '0.462rem' }}>
                      {transaction.editorialTitle || transaction.productTitle || 'Unknown'}
                    </td>
                    <td style={{ padding: '0.462rem', textAlign: 'right', fontSize: '0.612rem', color: '#666' }}>
                      {transaction.timestamp ? new Date(transaction.timestamp.seconds * 1000).toLocaleDateString() : 'Recent'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            padding: '1.387rem', 
            textAlign: 'center', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '3.699px',
            color: '#666'
          }}>
            <div style={{ fontSize: '1.387rem', marginBottom: '0.462rem' }}>⏱️</div>
            <h3 style={{ margin: '0 0 0.231rem 0', color: '#333' }}>No Activity Yet</h3>
            <p style={{ margin: 0 }}>Revenue tracking data will appear here as users interact with your editorial and shop content.</p>
          </div>
        )}
      </div>

      {/* Status Message */}
      {message && (
        <div style={{
          marginTop: '0.462rem',
          padding: '0.68rem',
          backgroundColor: message.includes('❌') ? '#fee' : '#efe',
          color: message.includes('❌') ? '#c33' : '#363',
          borderRadius: '4.08px',
          fontSize: '0.416rem'
        }}>
          {message}
        </div>
      )}
    </div>
  );
};

export default RevenueTracking;